import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import Footer from '../components/Footer'
import { useUser } from '../contexts/UserContext'
import { queries } from '../utils/queryOptimizer'
import { CITY_COORDS, FALLBACK_PRAYER_TIMES, DAYS_OF_WEEK } from '../utils/dashboardConstants'
import {
  WelcomeBanner,
  AdminStatsCards,
  AdminCharts,
  ProjectStatsCards,
  DashboardCalendar,
  ProductivityWidget,
  PrayerTimesWidget,
  ActivityNotesSection,
  ProjectsGrid,
  AddEventModal
} from '../components/dashboard'
import '../styles/dashboard.css'

export default function Dashboard() {
  const { userInfo, loading: userLoading } = useUser()
  const [projects, setProjects] = useState([])
  const [loadingProjects, setLoadingProjects] = useState(true)
  const [authUserCreatedAt, setAuthUserCreatedAt] = useState(null)
  const [currentDate, setCurrentDate] = useState(new Date())

  // Notes/To-Do List state
  const [notes, setNotes] = useState([])
  const [newNote, setNewNote] = useState('')

  // User Statistics state
  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    totalUsersCreated: 0,
    totalLoggedIn: 0,
    activeUsers: 0,
    deletedUsers: 0
  })
  const [loadingStats, setLoadingStats] = useState(true)

  // Weekly user statistics for bar chart
  const [weeklyStats, setWeeklyStats] = useState([])

  // Prayer times and clock state
  const [prayerTimes, setPrayerTimes] = useState(null)
  const [selectedCity, setSelectedCity] = useState('Jakarta')
  const [currentTime, setCurrentTime] = useState(new Date())

  // Productivity widget state
  const [todayAttendance, setTodayAttendance] = useState(null)
  const [todayTimesheet, setTodayTimesheet] = useState([])
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([])
  const [leaveStatus, setLeaveStatus] = useState({ pending: 0, approved: 0, total: 0 })
  const [userLeaves, setUserLeaves] = useState([])
  const [companyEvents, setCompanyEvents] = useState([])
  const [showAddEventModal, setShowAddEventModal] = useState(false)
  const [addEventForm, setAddEventForm] = useState({
    title: '',
    event_date: new Date().toISOString().split('T')[0],
    description: '',
    color: 'bg-teal-400'
  })
  const [isSubmittingEvent, setIsSubmittingEvent] = useState(false)
  const [loadingProductivity, setLoadingProductivity] = useState(true)
  const [prayerTimesError, setPrayerTimesError] = useState(null)
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  const productivityIntervalRef = useRef(null)
  const navigate = useNavigate()

  // ─── Data Fetching ───────────────────────────────────────────

  const fetchProductivityData = useCallback(async () => {
    if (!userInfo?.id) return

    try {
      setLoadingProductivity(true)
      const today = new Date().toISOString().split('T')[0]

      const [attendanceResult, timesheetResult, deadlinesResult, leavesResult, eventsResult] = await Promise.allSettled([
        supabase
          .from('attendance')
          .select('*')
          .eq('user_id', userInfo.id)
          .eq('date', today)
          .maybeSingle(),
        supabase
          .from('timesheet')
          .select('*')
          .eq('user_id', userInfo.id)
          .eq('date', today),
        supabase
          .from('projects')
          .select('*')
          .eq('user_id', userInfo.id)
          .gte('end_date', today)
          .lte('end_date', new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
          .order('end_date', { ascending: true })
          .limit(3),
        supabase
          .from('leave_requests')
          .select('*')
          .eq('user_id', userInfo.id),
        supabase
          .from('company_events')
          .select('*')
          .gte('event_date', new Date(today.split('-')[0], today.split('-')[1] - 1, 1).toISOString().split('T')[0])
          .lte('event_date', new Date(today.split('-')[0], today.split('-')[1], 0).toISOString().split('T')[0])
      ])

      if (attendanceResult.status === 'fulfilled' && !attendanceResult.value.error) {
        setTodayAttendance(attendanceResult.value.data)
      }
      if (timesheetResult.status === 'fulfilled') {
        setTodayTimesheet(timesheetResult.value.data || [])
      }
      if (deadlinesResult.status === 'fulfilled') {
        setUpcomingDeadlines(deadlinesResult.value.data || [])
      }
      if (leavesResult.status === 'fulfilled') {
        const leaves = leavesResult.value.data || []
        const pending = leaves.filter(l => l.status === 'pending').length
        const approved = leaves.filter(l => l.status === 'approved').length
        setLeaveStatus({ pending, approved, total: leaves.length })
        setUserLeaves(leaves.filter(l => l.status === 'approved'))
      }
      if (eventsResult.status === 'fulfilled' && !eventsResult.value.error) {
        setCompanyEvents(eventsResult.value.data || [])
      }
    } catch (error) {
      console.error('Error fetching productivity data:', error)
    } finally {
      setLoadingProductivity(false)
    }
  }, [userInfo?.id])

  const fetchAuthUserCreatedAt = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (user?.created_at) {
        setAuthUserCreatedAt(user.created_at)
      }
    } catch (error) {
      console.error('Error fetching auth user:', error)
    }
  }

  const updateOnlineCount = async () => {
    try {
      const { data: onlineCount, error } = await supabase.rpc('count_online_users')
      if (!error && onlineCount !== null) {
        setUserStats(prev => ({ ...prev, totalLoggedIn: onlineCount }))
      }
    } catch (err) {
      console.error('Error updating online count:', err)
    }
  }

  const fetchUserStats = async () => {
    setLoadingStats(true)
    try {
      const { count: totalUsers, error: usersError } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
      if (usersError) throw usersError

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const { count: createdToday, error: createdError } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', today.toISOString())
      if (createdError) throw createdError

      let loggedInUsers = 0
      try {
        const { data: onlineCount, error: onlineError } = await supabase.rpc('count_online_users')
        if (!onlineError && onlineCount !== null) {
          loggedInUsers = onlineCount
        } else {
          const twoMinutesAgo = new Date()
          twoMinutesAgo.setMinutes(twoMinutesAgo.getMinutes() - 2)
          const { count: onlineCount2, error: presenceError } = await supabase
            .from('user_presence')
            .select('user_id', { count: 'exact', head: true })
            .eq('status', 'online')
            .or(`heartbeat_at.is.null,heartbeat_at.gte.${twoMinutesAgo.toISOString()}`)
          if (!presenceError) loggedInUsers = onlineCount2 || 0
        }
      } catch (err) {
        console.error('Error fetching online users:', err)
        loggedInUsers = 0
      }

      let activeUsers = 0
      try {
        const weekAgo = new Date()
        weekAgo.setDate(weekAgo.getDate() - 7)
        const { count: activeCount, error: activeError } = await supabase
          .from('user_presence')
          .select('user_id', { count: 'exact', head: true })
          .gte('heartbeat_at', weekAgo.toISOString())
        if (!activeError && activeCount !== null) activeUsers = activeCount
      } catch {
        activeUsers = 0
      }

      let deletedUsers = 0
      try {
        const { data: deletedUsersData, count: deletedUsersCount, error: deletedError } = await supabase
          .from('user_deletions')
          .select('id', { count: 'exact' })
        if (deletedError) {
          console.error('❌ Error fetching deleted users:', deletedError)
          const { data: allDeletedUsers, error: fallbackError } = await supabase
            .from('user_deletions')
            .select('*')
          if (!fallbackError && allDeletedUsers) deletedUsers = allDeletedUsers.length
        } else {
          deletedUsers = deletedUsersCount || (deletedUsersData?.length) || 0
        }
      } catch (err) {
        console.error('❌ Exception fetching deleted users:', err)
        deletedUsers = 0
      }

      setUserStats({
        totalUsers: totalUsers || 0,
        totalUsersCreated: createdToday || 0,
        totalLoggedIn: loggedInUsers || 0,
        activeUsers: activeUsers || 0,
        deletedUsers: deletedUsers || 0
      })

      // Fetch weekly statistics for bar chart
      const weeklyData = []
      for (let i = 6; i >= 0; i--) {
        const date = new Date()
        date.setDate(date.getDate() - i)
        date.setHours(0, 0, 0, 0)
        const nextDate = new Date(date)
        nextDate.setDate(nextDate.getDate() + 1)

        const { count: newUsers } = await supabase
          .from('users')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', date.toISOString())
          .lt('created_at', nextDate.toISOString())

        weeklyData.push({
          day: DAYS_OF_WEEK[date.getDay()],
          users: newUsers || 0,
          date: date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })
        })
      }
      setWeeklyStats(weeklyData)
    } catch (error) {
      console.error('Error fetching user stats:', error)
      setUserStats({ totalUsers: 0, totalUsersCreated: 0, totalLoggedIn: 0, activeUsers: 0, deletedUsers: 0 })
      setWeeklyStats([])
    } finally {
      setLoadingStats(false)
    }
  }

  const fetchProjects = async () => {
    setLoadingProjects(true)
    try {
      const data = await queries.getProjects(userInfo.id)
      if (data) setProjects(data)
    } catch (error) {
      console.error('Error fetching projects:', error)
    } finally {
      setLoadingProjects(false)
    }
  }

  // ─── Event Handlers ──────────────────────────────────────────

  const handleDelete = async (projectId) => {
    const confirm = window.confirm('Yakin ingin menghapus project ini?')
    if (!confirm) return
    const { error } = await supabase.from('projects').delete().eq('project_id', projectId)
    if (error) {
      alert('Gagal hapus project: ' + error.message)
    } else {
      fetchProjects()
    }
  }

  const handleEdit = (projectId) => {
    navigate(`/edit-project/${projectId}`)
  }

  // Notes functions
  const addNote = () => {
    if (newNote.trim()) {
      const note = {
        id: Date.now(),
        text: newNote,
        completed: false,
        createdAt: new Date().toISOString()
      }
      setNotes([...notes, note])
      setNewNote('')
      localStorage.setItem('dashboard-notes', JSON.stringify([...notes, note]))
    }
  }

  const toggleNote = (id) => {
    const updatedNotes = notes.map(note =>
      note.id === id ? { ...note, completed: !note.completed } : note
    )
    setNotes(updatedNotes)
    localStorage.setItem('dashboard-notes', JSON.stringify(updatedNotes))
  }

  const deleteNote = (id) => {
    const updatedNotes = notes.filter(note => note.id !== id)
    setNotes(updatedNotes)
    localStorage.setItem('dashboard-notes', JSON.stringify(updatedNotes))
  }

  // ─── Effects ─────────────────────────────────────────────────

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Auth redirect
  useEffect(() => {
    if (!userLoading && !userInfo) navigate('/login')
  }, [userLoading, userInfo, navigate])

  // Initial data fetch + auto-refresh
  useEffect(() => {
    if (userInfo?.id) {
      fetchProjects()
      fetchAuthUserCreatedAt()
      fetchUserStats()
      fetchProductivityData()

      productivityIntervalRef.current = setInterval(() => {
        fetchProductivityData()
      }, 300000)
    }
    return () => {
      if (productivityIntervalRef.current) clearInterval(productivityIntervalRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.id, fetchProductivityData])

  // Admin subscriptions
  useEffect(() => {
    if (userInfo?.role !== 'admin') return

    const deletionsSubscription = supabase
      .channel('user_deletions_changes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'user_deletions' },
        () => setTimeout(() => fetchUserStats(), 500)
      )
      .subscribe()

    const usersSubscription = supabase
      .channel('users_table_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'users' },
        () => setTimeout(() => fetchUserStats(), 500)
      )
      .subscribe()

    const presenceSubscription = supabase
      .channel('user_presence_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_presence' },
        () => updateOnlineCount()
      )
      .subscribe()

    return () => {
      supabase.removeChannel(deletionsSubscription)
      supabase.removeChannel(usersSubscription)
      supabase.removeChannel(presenceSubscription)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.id, userInfo?.role])

  // Date update at midnight
  useEffect(() => {
    let dailyInterval
    const updateDate = () => setCurrentDate(new Date())
    const now = new Date()
    const midnight = new Date(now)
    midnight.setHours(24, 0, 0, 0)
    const msUntilMidnight = midnight.getTime() - now.getTime()

    const midnightTimer = setTimeout(() => {
      updateDate()
      dailyInterval = setInterval(updateDate, 24 * 60 * 60 * 1000)
    }, msUntilMidnight)

    return () => {
      clearTimeout(midnightTimer)
      if (dailyInterval) clearInterval(dailyInterval)
    }
  }, [])

  // Clock update every second
  useEffect(() => {
    let timeoutId
    const updateClock = () => {
      const now = new Date()
      setCurrentTime(now)
      const msUntilNextSecond = 1000 - now.getMilliseconds()
      timeoutId = setTimeout(updateClock, msUntilNextSecond)
    }
    updateClock()
    return () => clearTimeout(timeoutId)
  }, [])

  // Fetch prayer times
  useEffect(() => {
    const fetchPrayerTimes = async () => {
      if (!isOnline) {
        setPrayerTimes(FALLBACK_PRAYER_TIMES)
        setPrayerTimesError('Offline - Jadwal perkiraan')
        return
      }

      try {
        setPrayerTimesError(null)
        const today = new Date()
        const year = today.getFullYear()
        const month = today.getMonth() + 1
        const day = today.getDate()

        const coords = CITY_COORDS[selectedCity]
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000)

        const response = await fetch(
          `https://api.aladhan.com/v1/timings/${day}-${month}-${year}?latitude=${coords.lat}&longitude=${coords.lng}&method=20`,
          { signal: controller.signal }
        )

        clearTimeout(timeoutId)
        const data = await response.json()

        if (data.code === 200) {
          setPrayerTimes(data.data.timings)
          setPrayerTimesError(null)
        } else {
          setPrayerTimes(FALLBACK_PRAYER_TIMES)
          setPrayerTimesError('API Error - Jadwal perkiraan')
        }
      } catch (error) {
        console.error('Error fetching prayer times:', error)
        setPrayerTimes(FALLBACK_PRAYER_TIMES)
        if (error.name === 'AbortError') {
          setPrayerTimesError('Timeout - Jadwal perkiraan')
        } else {
          setPrayerTimesError('Offline - Jadwal perkiraan')
        }
      }
    }

    fetchPrayerTimes()
  }, [selectedCity, isOnline])

  // Load notes from localStorage
  useEffect(() => {
    const savedNotes = localStorage.getItem('dashboard-notes')
    if (savedNotes) setNotes(JSON.parse(savedNotes))
  }, [])

  // ─── Render ──────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {userLoading ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="relative inline-block">
              <div className="w-16 h-16 relative">
                <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-blue-500 border-r-blue-500 animate-spin"></div>
                <div className="absolute inset-2 rounded-full border-[3px] border-transparent border-b-blue-500 border-l-pink-500 animate-spin-reverse"></div>
                <div className="absolute inset-4 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 animate-pulse shadow-lg"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full shadow-md animate-ping"></div>
                </div>
              </div>
            </div>
            <p className="mt-6 text-gray-700 font-medium text-base animate-pulse">Loading dashboard...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Soft Futuristic Background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-100/30 via-blue-100/30 to-blue-100/30 rounded-full blur-3xl animate-float"></div>
            <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tl from-blue-100/30 via-pink-100/30 to-blue-100/30 rounded-full blur-3xl animate-float-delayed"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-cyan-50/20 via-blue-50/20 to-blue-50/20 rounded-full blur-3xl animate-float-slow"></div>
            <div className="absolute inset-0 dashboard-grid-pattern"></div>
          </div>

          <main className="relative z-10 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
              {/* Welcome Banner */}
              <WelcomeBanner userInfo={userInfo} />

              {/* Admin Stats Cards */}
              {userInfo?.role === 'admin' && (
                <AdminStatsCards
                  userStats={userStats}
                  loadingStats={loadingStats}
                  fetchUserStats={fetchUserStats}
                />
              )}

              {/* Admin Charts */}
              {userInfo?.role === 'admin' && !loadingStats && (
                <AdminCharts userStats={userStats} weeklyStats={weeklyStats} />
              )}

              {/* Project Stats Cards */}
              <ProjectStatsCards
                projects={projects}
                userInfo={userInfo}
                authUserCreatedAt={authUserCreatedAt}
                userLoading={userLoading}
                loadingProjects={loadingProjects}
              />

              {/* Calendar & Productivity */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <DashboardCalendar
                  currentDate={currentDate}
                  projects={projects}
                  userLeaves={userLeaves}
                  companyEvents={companyEvents}
                  userInfo={userInfo}
                  onAddEvent={() => setShowAddEventModal(true)}
                />
                <ProductivityWidget
                  todayAttendance={todayAttendance}
                  todayTimesheet={todayTimesheet}
                  upcomingDeadlines={upcomingDeadlines}
                  leaveStatus={leaveStatus}
                  loadingProductivity={loadingProductivity}
                  fetchProductivityData={fetchProductivityData}
                />
              </div>

              {/* Prayer Times & World Clocks */}
              <PrayerTimesWidget
                prayerTimes={prayerTimes}
                selectedCity={selectedCity}
                setSelectedCity={setSelectedCity}
                currentTime={currentTime}
                isOnline={isOnline}
                prayerTimesError={prayerTimesError}
              />

              {/* Activity Stats, Notes & Tips */}
              <ActivityNotesSection
                todayTimesheet={todayTimesheet}
                userStats={userStats}
                notes={notes}
                newNote={newNote}
                setNewNote={setNewNote}
                addNote={addNote}
                toggleNote={toggleNote}
                deleteNote={deleteNote}
              />

              {/* Projects Grid */}
              <ProjectsGrid
                projects={projects}
                loadingProjects={loadingProjects}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
              />
            </div>
          </main>
          <Footer />
        </>
      )}

      {/* Add Event Modal */}
      <AddEventModal
        showAddEventModal={showAddEventModal}
        setShowAddEventModal={setShowAddEventModal}
        addEventForm={addEventForm}
        setAddEventForm={setAddEventForm}
        isSubmittingEvent={isSubmittingEvent}
        setIsSubmittingEvent={setIsSubmittingEvent}
        userInfo={userInfo}
        fetchProductivityData={fetchProductivityData}
      />
    </div>
  )
}
